import React, { useState, useRef } from "react";
import BarcodeScanner from "react-qr-barcode-scanner";
import { BrowserMultiFormatReader } from "@zxing/library";
import { motion } from "framer-motion";
import { Scan } from "phosphor-react";

const QrScannerCard = ({ onScanResult }) => {
  const [data, setData] = useState("No result");
  const [stopStream, setStopStream] = useState(false);
  const fileInputRef = useRef(null);

  const handleUpdate = (err, result) => {
    if (result) {
      const text = result.text ?? result.getText?.();
      setData(text);
      onScanResult?.(text);
      setStopStream(true);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const imageData = reader.result;
        const codeReader = new BrowserMultiFormatReader();
        const result = await codeReader.decodeFromImageUrl(imageData);
        const text = result.text;
        setData(text);
        onScanResult?.(text);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error("QR decode failed:", error);
      setData("❌ Failed to read QR from image");
    }
  };

  return (
    <motion.div
      className="qr-card card border-0 shadow-sm rounded-4 mx-auto p-4 theme-card-white"
      initial={{ opacity: 0, y: 25 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
        <div className="icon-wrapper mx-auto mb-2">
          <Scan size={32} className="text-theme" />
        </div>
        <h5 className="fw-semibold theme-text-black pb-1">Scan or Upload QR Code</h5>
        <p className="text-muted text-sm mb-0 theme-text-color mb-4">Scan a QR code to make a payment.</p>

        {/* Live Scanner */}
        <div className="scanner-wrapper rounded-3 overflow-hidden mb-4">
          {!stopStream ? (
            <BarcodeScanner
              width={"100%"}
              height={"100%"}
              onUpdate={handleUpdate}
              facingMode="environment"
            />
          ) : (
            <div className="d-flex align-items-center justify-content-center h-100 bg-dark text-success fw-medium fs-5">
              ✅ Scan Complete
            </div>
          )}
        </div>

        {/* Upload Button */}
        <div className="d-flex align-items-center justify-content-center">
          <motion.button
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            onClick={() => fileInputRef.current.click()}
            className="theme-btn"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              width="20"
              height="20"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 16l4-4a3 3 0 014 0l4 4 4-4a3 3 0 014 0l2 2M3 21h18a2 2 0 002-2V5a2 2 0 00-2-2H3a2 2 0 00-2 2v14a2 2 0 002 2z"
              />
            </svg>
            <span>Upload from Gallery</span>
          </motion.button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="d-none"
          onChange={handleFileSelect}
        />

        {/* Result Display */}
        <motion.div
          className={`alert result-box ${data.startsWith("❌")
              ? "alert-danger"
              : data === "No result"
                ? "alert-secondary"
                : "alert-success"
            } mt-3`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          {data}
        </motion.div>
    </motion.div>
  );
};

export default QrScannerCard;
